import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  createMuiTheme,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteUser,
  getUserById,
  postUser,
  updateUser,
} from "../../../../api/user";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { shallowEqual, useSelector } from "react-redux";
import { Autocomplete } from "@material-ui/lab";
import {
  canEditAdminsModule,
  isOperario,
  isReadOnlyConsultor,
} from "../../../../_helpers/backofficePermissions";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyAdmin() {
  return {
    fullName: "",
    email: "",
    role: "admin",
    password: "",
    repeatPassword: "",
    active: true,
  };
}

export default function EditAdminsPage() {
  const [admin, setAdmin] = useState(getEmptyAdmin());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isGestor, setIsGestor] = useState(false);
  const [newPassword, setNewPassword] = useState({
    password: null,
    repeatPassword: null,
  });
  const [changePassword, setChangePassword] = useState(false);
  // Estados para controlar la visibilidad de las contraseñas
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const adminId = useParams().id;
  const history = useHistory();
  const user = useSelector((store) => store.authentication?.user, shallowEqual);
  const formReadOnly =
    isReadOnlyConsultor(user?.role) ||
    (isOperario(user?.role) && adminId && adminId !== user?._id);

  const roles = ["admin", "operario", "consultor", "congreso_delegado"];
  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!adminId) {
      disableLoadingData();
      if (!canEditAdminsModule(user?.role)) {
        alertError({ customMessage: "Solo para administradores" });
        history.push("/admins");
      } else {
        return;
      }
    }
    getUserById(adminId)
      .then((res) => {
        if (res.status === 200) {
          const user = res.data;
          delete user.password;
          setAdmin(user);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not get admin." });
        history.push("/admins");
      });
  }, [adminId, disableLoadingData, history]);

  function saveAdmin() {
    if (formReadOnly) {
      alertError({
        customMessage: "No tienes permiso para guardar cambios aquí.",
      });
      return;
    }
    let saveAdmin = admin;
    if (!adminId || changePassword) {
      if (!newPassword.password || !newPassword.repeatPassword) {
        alertError({
          error: null,
          customMessage: "Please enter the password.",
        });
        return;
      }
      if (newPassword.password !== newPassword.repeatPassword) {
        alertError({ error: null, customMessage: "Passwords do not match." });
        return;
      }
      saveAdmin = { ...saveAdmin, password: newPassword.password };
    }

    if (!adminId) {
      postUser(saveAdmin)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Admin successfully created.",
            });
            history.push("/admins");
          }
        })
        .catch((error) => {
          alertError({ error: error, customMessage: "Could not save admin." });
        });
    } else {
      updateUser(adminId, saveAdmin)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/admins");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element, valor) => (event) => {
    if (valor) {
      setAdmin({ ...admin, [element]: valor });
    } else {
      setAdmin({ ...admin, [element]: event.target.value });
    }
  };

  const handleChangeSelect = (element, valor) => {
    if (valor) {
      setAdmin({ ...admin, [element]: valor });
    }
  };

  // Funciones para toggle de visibilidad de contraseñas
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowRepeatPassword = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // Función para renderizar botones con diseño sticky
  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
    readOnly = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  {!readOnly && (
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>
                  )}

                  {showDelete && !readOnly && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <div style={{ paddingBottom: "80px" }}>
          {" "}
          {/* Espacio para botones sticky */}
          <Card>
            {/* Renderizar botones sticky */}
            {renderStickyButtons({
              onBack: () => history.push("/admins"),
              onSave: saveAdmin,
              onDelete: () => setOpenConfirmDialog(true),
              showDelete: !!adminId && canEditAdminsModule(user?.role),
              readOnly: formReadOnly,
              setObj: setAdmin,
              obj: admin,
            })}
            <CardHeader title="Edit admin"></CardHeader>
            <CardBody>
              <TextField
                id={`fullName`}
                label="Full name"
                value={admin.fullName}
                onChange={handleChange("fullName")}
                InputLabelProps={{
                  shrink: true,
                }}
                margin="normal"
                variant="outlined"
                required
              />
              <TextField
                id={`email`}
                label="Email"
                value={admin.email}
                onChange={handleChange("email")}
                InputLabelProps={{
                  shrink: true,
                }}
                type="email"
                margin="normal"
                variant="outlined"
                required
              />
              {!adminId || changePassword ? (
                <>
                  <br />
                  <br />
                  <TextField
                    id={`password`}
                    label="Password"
                    value={newPassword.password || ""}
                    onChange={(event) => {
                      if (event.target.value !== " ")
                        setNewPassword({
                          ...newPassword,
                          password: event.target.value,
                        });
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type={showPassword ? "text" : "password"}
                    margin="normal"
                    variant="outlined"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    id={`repeatPassword`}
                    label="Repeat password"
                    value={newPassword.repeatPassword || ""}
                    onChange={(event) => {
                      if (event.target.value !== " ")
                        setNewPassword({
                          ...newPassword,
                          repeatPassword: event.target.value,
                        });
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type={showRepeatPassword ? "text" : "password"}
                    margin="normal"
                    variant="outlined"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle repeat password visibility"
                            onClick={handleClickShowRepeatPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showRepeatPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <br />
                  <br />
                  {adminId && (
                    <>
                      <Button
                        onClick={() => {
                          setChangePassword(false);
                          setNewPassword({
                            password: null,
                            repeatPassword: null,
                          });
                          // Resetear visibilidad de contraseñas
                          setShowPassword(false);
                          setShowRepeatPassword(false);
                        }}
                        variant="outlined"
                        style={{ marginRight: "20px" }}
                      >
                        Cancel change password
                      </Button>
                      <br />
                      <br />
                    </>
                  )}
                </>
              ) : (
                <>
                  <br />
                  <br />
                  <Button
                    onClick={() => setChangePassword(true)}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Change password
                  </Button>
                  <br />
                  <br />
                </>
              )}
              {canEditAdminsModule(user?.role) && !formReadOnly && (
                <Autocomplete
                  className="w-100 mr-5"
                  id="autocomplete-familia"
                  options={roles}
                  // getOptionLabel={(option) => option?.titulo}
                  value={admin.role || ""}
                  onChange={(event, selected) => {
                    console.log(selected);
                    handleChangeSelect("role", selected);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Seleccionar"
                    />
                  )}
                />
              )}

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={admin.active || false}
                    onChange={() =>
                      setAdmin({ ...admin, active: !admin.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
            </div>
            </CardBody>
          </Card>
        </div>

        {/* Diálogo de confirmación */}
        <ConfirmDialog
          title={"Are you sure you want to delete this admin?"}
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          onConfirm={() => {
            deleteUser(adminId)
              .then((res) => {
                if (res.status === 204 || res.status === 200) {
                  alertSuccess({
                    title: "Deleted!",
                    customMessage: "Admin deleted successfully",
                  });
                  history.push("/admins");
                }
              })
              .catch((error) => {
                alertError({
                  error: error,
                  customMessage: "Could not delete admin.",
                });
              });
          }}
        />
      </>
    );
}
